import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import classNames from 'classnames';
import Typography from 'carbon-react/lib/components/typography';
import Link from 'carbon-react/lib/components/link';

import { FormattedMessage } from '@futrli/localisation';
import { isPending, useWindowSize } from '@futrli/shared-services';
import { UISystemIcon } from '@futrli/ui-components';

import { Storage } from '../../storage/storage';
import { StoreState } from '../../core/types';
import { getErrorSelector } from '../../core/redux-utils/selectors';
import { FORGOT_PASSWORD } from '../constants';
import { forgotPassword } from '../redux-utils/actions';
import { DESKTOP_LAYOUT_LIMIT, LOGIN_ROUTE } from '../../core/constants';
import styles from '../../core/common.scss';

export const ForgotPasswordSent: React.FC = () => {
  const flags = useFlags();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { width: windowWidth } = useWindowSize();

  const username = Storage.getInstance().get('username', '');

  const [isLoading, error] = useSelector((state: StoreState) => [
    isPending(state.loadingRequests.FORGOT_PASSWORD),
    getErrorSelector(state.authReducers, FORGOT_PASSWORD.ACTION)
  ]);

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const handleResendConfirmationClick = () => {
    dispatch(forgotPassword(navigate, username));
  };

  const handleLoginClick = () => navigate(LOGIN_ROUTE);

  if (!flags || Object.keys(flags).length === 0) {
    return null;
  }

  return (
    <main className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={16}>
            <FormattedMessage id="forgot_password.please_check_your_inbox" defaultMessage="Please check your inbox" />
          </Typography>
          <Typography textAlign="center" marginBottom={24} fontSize="16px">
            <FormattedMessage
              id="registered.no_email_check_spam"
              defaultMessage="Didn’t receive an email? Check your spam folder or {link}."
              values={{
                link: isLoading ? (
                  <FormattedMessage id="common.sending_progress" defaultMessage="Sending..." />
                ) : (
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={handleResendConfirmationClick}
                  >
                    <FormattedMessage id="common.resend_email" defaultMessage="Resend Email" />
                  </span>
                )
              }}
            />
          </Typography>
          {error ? (
            <Typography fontSize="14px" color="errorRed" marginTop={15}>
              <FormattedMessage
                id="forgot_password.resend_confirmation_error"
                defaultMessage="Confirmation email limit has been reached. Please make sure you check your spam folder and wait for 30 minutes before attempting any additional resend email requests."
              />
            </Typography>
          ) : null}
          <div style={{ display: 'flex', marginBottom: 16 }}>
            <svg width="100%" height="1" viewBox="0 0 400 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0.5H400" stroke="#7F7F7F" strokeWidth="0.5" />
            </svg>
          </div>
          <Link onClick={handleLoginClick} className={styles.sageCarbonAuthLink}>
            <FormattedMessage id="forgot_password.back_to_log_in" defaultMessage="Back to Log in?" />
          </Link>
        </div>
      </div>
    </main>
  );
};
